import React, { useCallback, useEffect, useState } from 'react';
import {
  RefreshCw,
  CheckCircle2,
  XCircle,
  Image as ImageIcon,
  ChevronLeft,
  ChevronRight,
  Clock3,
  Upload,
  ShieldCheck,
} from 'lucide-react';
import { supabase } from '../../services/supabaseClient';

const PAGE_SIZE = 15;

const fmtDate = (value) => {
  if (!value) return '—';
  return new Date(value).toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const normalizeCardNumber = (value) => (value || '').replace(/\D/g, '');

const formatCardNumber = (value) => {
  const digits = normalizeCardNumber(value);
  return digits.replace(/(\d{4})(?=\d)/g, '$1 ').trim();
};

const validateCardNumber = (inputValue, existingValue) => {
  const digits = normalizeCardNumber(inputValue);
  if (!digits) {
    if (existingValue) return null;
    return 'Enter card number from the photo (16 digits, starts with 7860)';
  }
  if (digits.length !== 16 || !digits.startsWith('7860')) {
    return 'Card number must be 16 digits starting with 7860';
  }
  return null;
};

const isApprovedItem = (item) =>
  item.status === 'active' && !!item.physical_card_verified_at;

const getItemStatus = (item) => {
  if (isApprovedItem(item)) {
    return { label: 'Approved', tone: 'green' };
  }
  if (item.physical_card_image_url) {
    return { label: 'Awaiting review', tone: 'blue' };
  }
  return { label: 'Awaiting upload', tone: 'amber' };
};

const statusBadgeClass = (tone) => {
  if (tone === 'green') return 'bg-emerald-50 text-emerald-800 ring-emerald-200';
  if (tone === 'blue') return 'bg-sky-50 text-sky-800 ring-sky-200';
  return 'bg-amber-50 text-amber-800 ring-amber-200';
};

const TABS = [
  { id: 'pending', label: 'Pending' },
  { id: 'awaiting_review', label: 'Awaiting review' },
  { id: 'approved', label: 'Approved' },
  { id: 'all', label: 'All' },
];

const PhysicalCardVerifications = () => {
  const [items, setItems] = useState([]);
  const [cardNumbers, setCardNumbers] = useState({});
  const [stats, setStats] = useState({
    awaiting_upload: 0,
    awaiting_review: 0,
    approved: 0,
    pending_total: 0,
  });
  const [filter, setFilter] = useState('pending');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [actingId, setActingId] = useState(null);
  const [error, setError] = useState('');

  const loadItems = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data, error: rpcError } = await supabase.rpc('admin_list_physical_card_verifications', {
        p_filter: filter,
        p_page: page,
        p_page_size: PAGE_SIZE,
      });
      if (rpcError) throw rpcError;
      const nextItems = Array.isArray(data?.items) ? data.items : [];
      setItems(nextItems);
      setStats({
        awaiting_upload: Number(data?.stats?.awaiting_upload || 0),
        awaiting_review: Number(data?.stats?.awaiting_review || 0),
        approved: Number(data?.stats?.approved || 0),
        pending_total: Number(data?.stats?.pending_total || 0),
      });
      setTotal(Number(data?.total || 0));
      setTotalPages(Math.max(Number(data?.total_pages || 1), 1));
      setCardNumbers((prev) => {
        const next = { ...prev };
        nextItems.forEach((item) => {
          if (next[item.subscription_id] === undefined) {
            next[item.subscription_id] = formatCardNumber(item.membership_card_number || '');
          }
        });
        return next;
      });
    } catch (e) {
      setError(e.message || 'Failed to load verifications');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [filter, page]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const handleFilterChange = (nextFilter) => {
    setFilter(nextFilter);
    setPage(1);
  };

  const handleCardNumberChange = (subscriptionId, value) => {
    setCardNumbers((prev) => ({
      ...prev,
      [subscriptionId]: value,
    }));
  };

  const handleCardNumberBlur = (subscriptionId) => {
    setCardNumbers((prev) => ({
      ...prev,
      [subscriptionId]: formatCardNumber(prev[subscriptionId] || ''),
    }));
  };

  const handleVerify = async (item, approve) => {
    let rejectionReason = '';
    if (!approve) {
      rejectionReason = window.prompt('Rejection reason (optional):', 'Photo unclear — please upload again') || '';
    } else {
      const cardError = validateCardNumber(
        cardNumbers[item.subscription_id],
        item.membership_card_number,
      );
      if (cardError) {
        alert(cardError);
        return;
      }
    }

    setActingId(item.subscription_id);
    try {
      const payload = {
        p_subscription_id: item.subscription_id,
        p_approve: approve,
        p_rejection_reason: rejectionReason || null,
      };

      if (approve) {
        const digits = normalizeCardNumber(cardNumbers[item.subscription_id]);
        if (digits) {
          payload.p_membership_card_number = digits;
        }
      }

      const { data, error: rpcError } = await supabase.rpc('admin_verify_physical_card', payload);
      if (rpcError) throw rpcError;
      if (!data?.success) throw new Error(data?.error || 'Action failed');
      await loadItems();
    } catch (e) {
      alert(e.message || 'Could not update verification');
    } finally {
      setActingId(null);
    }
  };

  const safePage = Math.min(page, totalPages);
  const rangeStart = total === 0 ? 0 : (safePage - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(safePage * PAGE_SIZE, total);

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Physical Card Verification</h1>
          <p className="text-gray-600 mt-1 max-w-2xl">
            Review legacy membership card photos, enter card numbers, and activate RSA after approval.
          </p>
        </div>
        <button
          type="button"
          onClick={loadItems}
          disabled={loading}
          className="inline-flex items-center gap-2 self-start px-4 py-2 rounded-xl bg-white shadow-sm ring-1 ring-gray-200 hover:bg-gray-50 disabled:opacity-60"
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {error ? (
        <div className="rounded-xl bg-red-50 text-red-800 px-4 py-3 text-sm ring-1 ring-red-200">{error}</div>
      ) : null}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-gray-200">
          <div className="flex items-center gap-3">
            <div className="rounded-xl bg-amber-50 p-2.5 text-amber-700">
              <Upload size={18} />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Awaiting upload</p>
              <p className="text-2xl font-bold text-gray-900">{stats.awaiting_upload}</p>
            </div>
          </div>
        </div>
        <div className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-gray-200">
          <div className="flex items-center gap-3">
            <div className="rounded-xl bg-sky-50 p-2.5 text-sky-700">
              <Clock3 size={18} />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Awaiting review</p>
              <p className="text-2xl font-bold text-gray-900">{stats.awaiting_review}</p>
            </div>
          </div>
        </div>
        <div className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-gray-200">
          <div className="flex items-center gap-3">
            <div className="rounded-xl bg-emerald-50 p-2.5 text-emerald-700">
              <ShieldCheck size={18} />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Approved</p>
              <p className="text-2xl font-bold text-gray-900">{stats.approved}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm ring-1 ring-gray-200 overflow-hidden">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-4 py-3 border-b border-gray-100 bg-gray-50/80">
          <div className="inline-flex rounded-xl bg-white p-1 ring-1 ring-gray-200">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => handleFilterChange(tab.id)}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                  filter === tab.id
                    ? 'bg-gray-900 text-white shadow-sm'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                {tab.label}
                {tab.id === 'pending' ? ` (${stats.pending_total})` : null}
                {tab.id === 'awaiting_review' ? ` (${stats.awaiting_review})` : null}
                {tab.id === 'approved' ? ` (${stats.approved})` : null}
              </button>
            ))}
          </div>
          <p className="text-xs text-gray-500 px-1">
            {total} record{total === 1 ? '' : 's'} in this view
          </p>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-white text-left text-gray-500 border-b border-gray-100">
              <tr>
                <th className="px-4 py-3 font-semibold">Customer</th>
                <th className="px-4 py-3 font-semibold">Vehicle</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold min-w-[220px]">Card #</th>
                <th className="px-4 py-3 font-semibold">Photo</th>
                <th className="px-4 py-3 font-semibold">Updated</th>
                <th className="px-4 py-3 font-semibold">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-gray-500">Loading…</td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-gray-500">
                    No{' '}
                    {filter === 'approved'
                      ? 'approved'
                      : filter === 'awaiting_review'
                        ? 'awaiting review'
                        : filter === 'all'
                          ? ''
                          : 'pending'}{' '}
                    physical card records
                  </td>
                </tr>
              ) : (
                items.map((item) => {
                  const approved = isApprovedItem(item);
                  const itemStatus = getItemStatus(item);
                  const cardDigits = normalizeCardNumber(cardNumbers[item.subscription_id]);
                  const cardMissing = !approved && !cardDigits && !item.membership_card_number;
                  const cardInvalid = !approved && cardDigits
                    && (cardDigits.length !== 16 || !cardDigits.startsWith('7860'));

                  return (
                    <tr key={item.subscription_id} className="align-top hover:bg-gray-50/60">
                      <td className="px-4 py-4">
                        <div className="font-semibold text-gray-900">{item.customer_name || '—'}</div>
                        <div className="text-gray-500 mt-0.5">{item.customer_phone || '—'}</div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="font-medium text-gray-900">{item.vehicle_registration || '—'}</div>
                        <div className="text-gray-500 mt-0.5">{item.vehicle_model || '—'}</div>
                      </td>
                      <td className="px-4 py-4">
                        <span
                          className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ${statusBadgeClass(itemStatus.tone)}`}
                        >
                          {itemStatus.label}
                        </span>
                        {approved ? (
                          <div className="text-xs text-gray-500 mt-2">
                            Verified {fmtDate(item.physical_card_verified_at)}
                          </div>
                        ) : null}
                      </td>
                      <td className="px-4 py-4">
                        {approved ? (
                          <div className="font-mono text-xs text-gray-900 bg-gray-50 rounded-lg px-3 py-2 ring-1 ring-gray-200">
                            {formatCardNumber(item.membership_card_number) || '—'}
                          </div>
                        ) : (
                          <>
                            <input
                              type="text"
                              inputMode="numeric"
                              value={cardNumbers[item.subscription_id] ?? ''}
                              onChange={(e) => handleCardNumberChange(item.subscription_id, e.target.value)}
                              onBlur={() => handleCardNumberBlur(item.subscription_id)}
                              placeholder="7860 XXXX XXXX XXXX"
                              className={`w-full min-w-[200px] rounded-lg px-3 py-2 font-mono text-xs ring-1 ${
                                cardMissing && item.physical_card_image_url
                                  ? 'ring-amber-300 bg-amber-50'
                                  : cardInvalid
                                    ? 'ring-red-300 bg-red-50'
                                    : 'ring-gray-200 bg-white'
                              }`}
                            />
                            {cardMissing && item.physical_card_image_url ? (
                              <p className="text-xs text-amber-700 mt-1.5">Enter number from card photo before approve</p>
                            ) : (
                              <p className="text-xs text-gray-500 mt-1.5">Saved on approve</p>
                            )}
                          </>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        {item.physical_card_image_url ? (
                          <a
                            href={item.physical_card_image_url}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1.5 rounded-lg bg-sky-50 px-2.5 py-1.5 text-sky-800 ring-1 ring-sky-200 hover:bg-sky-100"
                          >
                            <ImageIcon size={14} />
                            View photo
                          </a>
                        ) : (
                          <span className="inline-flex items-center rounded-lg bg-amber-50 px-2.5 py-1.5 text-xs font-medium text-amber-800 ring-1 ring-amber-200">
                            Not uploaded
                          </span>
                        )}
                        {item.physical_card_rejection_reason ? (
                          <div className="text-xs text-red-600 mt-2">{item.physical_card_rejection_reason}</div>
                        ) : null}
                      </td>
                      <td className="px-4 py-4 text-gray-500 whitespace-nowrap">
                        {approved ? fmtDate(item.physical_card_verified_at) : fmtDate(item.updated_at)}
                      </td>
                      <td className="px-4 py-4">
                        {approved ? (
                          <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold text-xs">
                            <CheckCircle2 size={14} />
                            Verified
                          </span>
                        ) : item.physical_card_image_url ? (
                          <div className="flex flex-wrap gap-2">
                            <button
                              type="button"
                              disabled={actingId === item.subscription_id}
                              onClick={() => handleVerify(item, true)}
                              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60"
                            >
                              <CheckCircle2 size={14} />
                              Approve
                            </button>
                            <button
                              type="button"
                              disabled={actingId === item.subscription_id}
                              onClick={() => handleVerify(item, false)}
                              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-60"
                            >
                              <XCircle size={14} />
                              Reject
                            </button>
                          </div>
                        ) : (
                          <span className="text-gray-400 text-xs">Waiting for customer</span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {!loading && total > 0 ? (
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-t border-gray-100 px-4 py-3 bg-gray-50/60">
            <p className="text-xs text-gray-500">
              Showing {rangeStart}–{rangeEnd} of {total}
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={safePage <= 1}
                className="inline-flex items-center gap-1 rounded-lg bg-white px-3 py-1.5 text-xs font-medium text-gray-700 ring-1 ring-gray-200 hover:bg-gray-50 disabled:opacity-40"
              >
                <ChevronLeft size={14} />
                Prev
              </button>
              <span className="text-xs font-medium text-gray-600 min-w-[88px] text-center">
                Page {safePage} / {totalPages}
              </span>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={safePage >= totalPages}
                className="inline-flex items-center gap-1 rounded-lg bg-white px-3 py-1.5 text-xs font-medium text-gray-700 ring-1 ring-gray-200 hover:bg-gray-50 disabled:opacity-40"
              >
                Next
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default PhysicalCardVerifications;
